/**
 * 에이전트 사이드바 메뉴 구조 정의
 *
 * ─────────────────────────────────────────────────────────────────
 * 메뉴 항목 추가·수정·삭제는 이 파일만 변경하면 됩니다.
 * LeftNav.tsx는 이 파일을 읽어 자동으로 렌더링합니다.
 * ─────────────────────────────────────────────────────────────────
 *
 * 레벨 구조
 * ─────────
 *   1레벨: 에이전트 목록 (스튜디오 홈 화면) or 에이전트 이름 헤더 (에이전트 진입 후)
 *   2레벨: AgentMenuConfig.children
 *          - children 없음 → 클릭 시 path로 직접 이동 (리프 메뉴)
 *          - children 있음 → 클릭 시 펼침/접힘 (그룹 헤더, 이동 없음)
 *   3레벨: SubMenuItem.children
 *          - 항상 리프 메뉴, 클릭 시 path로 직접 이동
 *
 * ─────────────────────────────────────────────────────────────────
 * 에이전트 팀 작업 가이드
 * ─────────────────────────────────────────────────────────────────
 *
 * [방식 A] URL 라우팅 기반 (권장)
 *   각 메뉴 항목이 독립된 URL 경로를 가집니다.
 *
 *   1) 이 파일에 메뉴 항목 추가 (path는 실제 URL 경로)
 *        { id: 'sites', label: '현장', path: '/agents/photo-organizer/sites' }
 *
 *   2) 에이전트 패키지의 routes.ts에 해당 경로 등록
 *        packages/agents/photo-organizer/src/routes.ts
 *        ───────────────────────────────────────────────
 *        export const routes: RouteObject[] = [
 *          { path: '/agents/photo-organizer',       Component: HomePage },
 *          { path: '/agents/photo-organizer/sites',  Component: SitesPage },
 *          { path: '/agents/photo-organizer/search', Component: SearchPage },
 *        ];
 *
 *   두 파일의 path가 일치해야 클릭 시 화면이 연결됩니다.
 *   path가 등록되지 않으면 클릭 시 404가 발생합니다.
 *
 * ─────────────────────────────────────────────────────────────────
 *
 * [방식 B] 내부 상태 기반 (기존 에이전트 호환)
 *   하나의 페이지 컴포넌트 안에서 view 상태로 화면을 전환합니다.
 *   (현재 인디플래너가 이 방식 사용)
 *
 *   1) 이 파일에 메뉴 항목 추가 (path는 베이스 경로 + ?view= 쿼리 파라미터)
 *        { id: 'gantt', label: '간트차트', path: '/agents/scheduler?view=gantt' }
 *
 *   2) 에이전트 routes.ts는 베이스 경로 하나만 유지
 *        { path: '/agents/scheduler', Component: SchedulerPage }
 *
 *   3) 에이전트 페이지에서 useSearchParams()로 초기 view 읽기
 *        const [searchParams] = useSearchParams()
 *        const [view, setView] = useState(searchParams.get('view') ?? 'home')
 *
 *   4) 내부 navigate 함수에서 URL도 함께 업데이트
 *        setSearchParams({ view: v }, { replace: true })
 *
 *   나중에 방식 A로 전환하려면 routes.ts에 경로를 추가하고
 *   이 파일의 path에서 ?view= 를 제거하면 됩니다.
 */

export interface SubSubMenuItem {
  id: string;
  label: string;
  path: string;
  /** true면 LeftNav 사이드바에는 노출하지 않는다(TopBar 브레드크럼 매칭 전용) —
   *  예: 위저드 내부 단계처럼 순서를 건너뛰고 직접 진입하면 안 되는 경로. */
  hideInNav?: boolean;
}

export interface SubMenuItem {
  id: string;
  label: string;
  path: string;
  children?: SubSubMenuItem[];
}

export interface AgentMenuConfig {
  agentId: string;        // FE 라우팅 슬러그
  numericAgentId: number; // DB agentId — 구독 여부 매핑용
  label: string;
  order: number;
  basePath: string;
  defaultPath: string;
  children: SubMenuItem[];
}

export const AGENT_MENU_CONFIG: AgentMenuConfig[] = [
  // ─── 인디포토 ──────────────────────────────────────────────────────────────
  {
    agentId: 'photo-organizer',
    numericAgentId: 1,
    label: '인디포토',
    order: 10,
    basePath: '/agents/photo-organizer',
    defaultPath: '/agents/photo-organizer/upload',
    children: [
      { id: 'upload',    label: '사진', path: '/agents/photo-organizer/upload' },
      { id: 'site-list', label: '현장',        path: '/agents/photo-organizer/site-list' },
      { id: 'search',    label: '검색',        path: '/agents/photo-organizer/search' },
      {
        id: 'share',
        label: '공유',
        path: '/agents/photo-organizer/share',
        children: [
          { id: 'share',         label: '공유하기',  path: '/agents/photo-organizer/share' },
          { id: 'share-history', label: '발송 이력', path: '/agents/photo-organizer/share-history' },
        ],
      },
    ],
  },

  // ─── 인디포트폴리오 (numericAgentId=11) ───────────────────────────────────
  // 기획안 LNB 순서: 인디포토(10) → 인디포트폴리오(15) → 인디플래너(20) → 인디공사현황(30)
  {
    agentId: 'portfolio',
    numericAgentId: 11,
    label: '인디포트폴리오',
    order: 15,
    basePath:    '/agents/portfolio',
    defaultPath: '/agents/portfolio/projects',
    children: [
      { id: 'write',    label: '시공사례 작성', path: '/agents/portfolio/write' },
      { id: 'projects', label: '시공사례 목록', path: '/agents/portfolio/projects' },
      { id: 'sns',      label: 'SNS 포스팅',   path: '/agents/portfolio/sns' },
    ],
  },

  // ─── 인디공사현황 (A02, numericAgentId=2) ────────────────────────────────
  {
    agentId: 'work-log',
    numericAgentId: 2,
    label: '인디공사현황',
    order: 30,
    basePath:    '/agents/work-log',
    defaultPath: '/agents/work-log',
    children: [
      { id: 'home',             label: '홈', path: '/agents/work-log' },
      {
        id: 'workorder',
        label: '작업요청',
        path: '/agents/work-log/workorder', // children 존재 → 클릭 시 펼침(이동 없음)
        children: [
          { id: 'workorder-list', label: '작업요청 목록', path: '/agents/work-log/workorder' },
          { id: 'workorder-new',  label: '작업요청 작성', path: '/agents/work-log/workorder/new' },
        ],
      },
      { 
        id: 'field-reports',
        label: '현장보고',
        path: '/agents/work-log/field-reports',
        children: [
          { id: 'field-reports-list', label: '현장보고 목록', path: '/agents/work-log/field-reports' },
          { id: 'field-reports-new',  label: '현장보고 작성', path: '/agents/work-log/field-reports/new' },
        ],
      },
      { id: 'site-reports',     label: '공사현황',        path: '/agents/work-log/site-reports' },
      // 고객리포트 설정·미리보기(구 children)는 진입점5(현장보고 편집·발송)로 통합되며 폐기됨(설계서 §5.6) — 단일 메뉴로 축소.
      { id: 'customer-reports', label: '현장보고 편집·발송', path: '/agents/work-log/customer-reports/edit' },
      /*{ id: 'management',       label: '공사관리',        path: '/agents/work-log/management' },
      { id: 'history',          label: '발송이력',        path: '/agents/work-log/history' },*/
    ],
  },

  // ─── 인디플래너 (E01, numericAgentId=8) ──────────────────────────────
  // 스케줄러는 내부 view 상태 기계 사용 (SchedulerPage.tsx NAV_GROUPS 기준)
  // path는 ?view= 쿼리 파라미터 방식 — 에이전트 팀이 useSearchParams() 연동 시 활성화
  {
    agentId: 'scheduler',
    numericAgentId: 8,
    label: '인디플래너',
    order: 20,
    basePath: '/agents/scheduler',
    defaultPath: '/agents/scheduler',
    children: [
      {
        id: 'home',
        label: '홈',
        path: '/agents/scheduler',
      },
      {
        id: 'create',
        label: '스케줄 생성',
        path: '/agents/scheduler?view=create-site',
        // 아래 항목 전부 TopBar 브레드크럼 매칭 전용 — 위저드 단계를 건너뛰고 사이드바에서
        // 직접 진입하면 안 되므로 LeftNav에는 노출하지 않는다(hideInNav).
        children: [
          { id: 'create-site',    label: '현장 선택',        path: '/agents/scheduler?view=create-site',    hideInNav: true },
          { id: 'create-upload',  label: '견적서 업로드',      path: '/agents/scheduler?view=create-upload',  hideInNav: true },
          { id: 'create-extract', label: '공종/공사일 선택', path: '/agents/scheduler?view=create-extract', hideInNav: true },
          // 자동 생성(견적서 → 자동 배정) 흐름 — 메뉴 145, 직접 생성은 위 항목들(메뉴 146)
          { id: 'create-upload2',      label: '견적서 업로드',      path: '/agents/scheduler?view=create-upload2',      hideInNav: true },
          { id: 'create-extract-raw2', label: '추출 결과 확인',     path: '/agents/scheduler?view=create-extract-raw2', hideInNav: true },
          { id: 'create-extract2',     label: '공종/공사일 선택', path: '/agents/scheduler?view=create-extract2',     hideInNav: true },
          { id: 'assign-preview',      label: '배정 확인',          path: '/agents/scheduler?view=assign-preview',      hideInNav: true },
        ],
      },
      {
        id: 'manage',
        label: '스케줄 관리',
        path: '/agents/scheduler?view=gantt',
        children: [
          { id: 'gantt',     label: '스케줄', path: '/agents/scheduler?view=gantt' },
          { id: 'cal-b2',    label: '캘린더', path: '/agents/scheduler?view=cal-b2' },
          { id: 'work-type', label: '리스트', path: '/agents/scheduler?view=work-type' },
        ],
      },
      {
        id: 'site-management',
        label: '현장 관리',
        path: '/agents/scheduler?view=site-list',
      },
      {
        id: 'share',
        label: '일정 공유',
        path: '/agents/scheduler?view=kakao',
        children: [
          { id: 'kakao',         label: '일정 전송', path: '/agents/scheduler?view=kakao' },
          { id: 'kakao-history', label: '전송 이력', path: '/agents/scheduler?view=kakao-history' },
        ],
      },
      {
        id: 'settings',
        label: '설정',
        path: '/agents/scheduler?view=settings-alarm',
        children: [
          { id: 'settings-alarm',    label: '알림',          path: '/agents/scheduler?view=settings-alarm' },
          { id: 'settings-duration', label: '공종별 소요일',       path: '/agents/scheduler?view=settings-duration' },
          { id: 'settings-order',    label: '발주-입고설정', path: '/agents/scheduler?view=settings-order' },
          { id: 'settings-partner',  label: '공종별 주 시공파트너', path: '/agents/scheduler?view=settings-partner' },
          { id: 'settings-foreman-confirm', label: '시공파트너 일정확인', path: '/agents/scheduler?view=settings-foreman-confirm' },
        ],
      },
    ],
  },

  // ─── 구독결제 (G/UTIL, numericAgentId 미정) ──────────────────────────────
  // ※ numericAgentId=99는 임시값. Member 팀이 DB agentId를 확정하면 실제 값으로 교체 필요.
  //   그 전까지는 구독 매핑(ids.has(numericAgentId))에 걸리지 않아 사이드바에 노출되지 않는다.
  {
    agentId: 'billing',
    numericAgentId: 99,
    label: '구독결제',
    order: 90,
    basePath: '/agents/billing',
    defaultPath: '/agents/billing',
    children: [
      { id: 'billing', label: '구독결제', path: '/agents/billing' },
    ],
  },
];
